import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth, api } from '../context/AuthContext';
import { browserTz, tzLabel, formatInTz, wallClockToUtc } from '../lib/tz';
import { grossFromNet, feeBreakdown, fmtMoney } from '../lib/fees';
import {
  ArrowLeft, Star, MapPin, MessageSquare, UserPlus, UserMinus,
  Clock, DollarSign, Users as UsersIcon, Award, X, Info,
} from 'lucide-react';
import './DetailedProfile.css';

const initials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || 'M';

const StarRow = ({ rating = 0, size = 14 }) => (
  <div className="dp-stars" aria-label={`${rating} out of 5`}>
    {[1,2,3,4,5].map(i => (
      <Star
        key={i}
        size={size}
        fill={i <= Math.round(rating) ? '#D97706' : 'transparent'}
        color={i <= Math.round(rating) ? '#D97706' : '#D1D5DB'}
      />
    ))}
  </div>
);

const FeeLine = ({ net }) => {
  const f = feeBreakdown(net);
  const m = (n) => `$${n.toFixed(2)}`;
  return (
    <p data-testid="booking-fee-breakdown" style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 12, margin: '-14px 0 18px' }}>
      {m(f.teacherNet)} mentor + {m(f.platformFee)} platform + {m(f.appStoreFee)} app store + {m(f.stripeFee)} Stripe
    </p>
  );
};

const DetailedProfile = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [reviewMeta, setReviewMeta] = useState({ average_rating: 0, total_ratings: 0 });
  const [booking, setBooking] = useState(null); // { pkg } — request session modal
  const [reqDate, setReqDate] = useState('');
  const [reqTime, setReqTime] = useState('13:00');
  const [reqNote, setReqNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [pRes, rRes] = await Promise.all([
          api(`/api/users/${id}`).catch(() => null),
          api(`/api/creators/${id}/ratings?limit=10`).catch(() => null),
        ]);
        if (cancelled) return;
        if (pRes) {
          setProfile(pRes.user);
          setIsFollowing(user?.following?.includes(id) || false);
        }
        if (rRes) {
          setReviews(rRes.ratings || []);
          setReviewMeta({
            average_rating: rRes.average_rating || 0,
            total_ratings: rRes.total_ratings || 0,
          });
        }
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [id, user]);

  const toggleFollow = async () => {
    if (!user) return toast.error('Please sign in to follow mates');
    try {
      if (isFollowing) {
        await api(`/api/users/${id}/follow`, { method: 'DELETE' });
        setIsFollowing(false);
        toast('Unfollowed', { description: `You no longer follow ${profile.full_name}.` });
      } else {
        await api(`/api/users/${id}/follow`, { method: 'POST' });
        setIsFollowing(true);
        toast.success('Following', { description: `You'll see ${profile.full_name}'s new videos in your feed.` });
      }
    } catch (e) { toast.error('Something went wrong', { description: e.message }); }
  };

  const bookSession = (pkg) => {
    if (!user) return toast.error('Please sign in to book a session');
    if (user.id === id) return toast('That\u2019s you', { description: 'You can\u2019t book your own package.' });
    // Open calendar-first request modal; the Stripe pre-auth pop-up only appears after Pay & Request.
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setReqDate(tomorrow.toISOString().slice(0, 10));
    setReqTime('13:00');
    setReqNote('');
    setBooking({ pkg });
  };

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const pkgId = searchParams.get('book');
    if (!pkgId || !profile) return;
    const pkg = (profile.booking_packages || []).find(p => p.id === pkgId);
    setSearchParams({}, { replace: true });
    if (pkg) bookSession(pkg);
  }, [profile]);

  const submitBookingRequest = async () => {
    if (!booking?.pkg) return;
    if (!reqDate || !reqTime) return toast.error('Pick a date and time first.');
    if (reqNote.trim().length < 5) return toast.error('Add a short message (min 5 chars) on what you\u2019d like to learn.');
    // The date/time inputs are labelled "MENTOR'S TIME", so convert the
    // chosen wall-clock from the mentor's IANA zone (fallback: viewer local)
    // into a real UTC instant.
    const mentorTz = profile?.timezone || '';
    const scheduled = wallClockToUtc(reqDate, reqTime, mentorTz);
    if (!scheduled || isNaN(scheduled)) return toast.error('That date/time doesn\u2019t look right.');
    if (scheduled.getTime() < Date.now() + 60 * 60 * 1000) return toast.error('Pick a time at least 1 hour from now.');
    setSubmitting(true);
    try {
      const params = new URLSearchParams({
        package_id: booking.pkg.id,
        creator_id: id,
        scheduled_date: scheduled.toISOString(),
        note: reqNote.trim(),
      });
      const data = await api(`/api/payments/checkout?${params.toString()}`, { method: 'POST' });
      if (data.checkout_url) {
        toast.success('Redirecting to secure checkout\u2026 you\u2019ll only be charged when the mentor joins the meeting.');
        window.location.href = data.checkout_url;
      } else {
        toast.error('Checkout unavailable', { description: 'Please try again in a moment.' });
      }
    } catch (err) {
      toast.error('Booking failed', { description: err.message || 'Please try again.' });
    }
    setSubmitting(false);
  };

  // The pair of times a student sees while picking a slot: the wall-clock in
  // the mentor's zone (what they typed) and the equivalent moment in their own
  // zone — so they know whether 3pm Sydney is 4am London for them.
  const scheduledIso = useMemo(() => {
    if (!reqDate || !reqTime) return null;
    const mentorTz = profile?.timezone || '';
    const d = wallClockToUtc(reqDate, reqTime, mentorTz);
    return d && !isNaN(d) ? d.toISOString() : null;
  }, [reqDate, reqTime, profile?.timezone]);

  const myTz = user?.timezone || browserTz();
  const mentorKnowsTz = !!(profile?.timezone);
  const mentorTz = profile?.timezone || myTz;
  const yourTime = useMemo(() => {
    if (!scheduledIso) return null;
    return formatInTz(scheduledIso, myTz);
  }, [scheduledIso, myTz]);
  const mentorTimeDisplay = useMemo(() => {
    if (!scheduledIso) return null;
    return formatInTz(scheduledIso, mentorTz);
  }, [scheduledIso, mentorTz]);
  // Only show the dual-timezone panel when (a) the mentor actually has a
  // timezone stored AND (b) it differs from the viewer's. Otherwise the two
  // panels would render identical wall-clocks — confusing and worse than
  // helpful.
  const showYourLocalPanel = mentorKnowsTz && mentorTz !== myTz;

  if (loading) return (
    <div className="dp-page" data-testid="detailed-profile-loading">
      <div className="dp-loading">Loading profile…</div>
    </div>
  );
  if (!profile) return (
    <div className="dp-page">
      <div className="dp-loading">Mate not found.</div>
    </div>
  );

  const showActions = user && user.id !== id;

  return (
    <div className="dp-page" data-testid="detailed-profile">
      {/* Sticky top bar */}
      <div className="dp-topbar">
        <button
          onClick={() => navigate(-1)}
          className="dp-icon-btn"
          data-testid="back-btn"
          aria-label="Back"
        >
          <ArrowLeft size={20} />
        </button>
        <h2 className="dp-topbar-title">@{profile.username}</h2>
        <span className="dp-icon-btn-placeholder" />
      </div>

      {/* Hero */}
      <section className="dp-hero">
        <div className="dp-avatar-wrap">
          {profile.avatar_url ? (
            <img src={profile.avatar_url} alt={profile.full_name} className="dp-avatar" />
          ) : (
            <div className="dp-avatar dp-avatar-placeholder">{initials(profile.full_name)}</div>
          )}
        </div>
        <h1 className="dp-name">{profile.full_name}</h1>
        {profile.location && (
          <p className="dp-location">
            <MapPin size={14} /> {profile.location}
          </p>
        )}
        {reviewMeta.total_ratings > 0 && (
          <div className="dp-review-summary" data-testid="dp-rating-summary">
            <StarRow rating={reviewMeta.average_rating} size={16} />
            <span className="dp-rating-num">{reviewMeta.average_rating.toFixed(1)}</span>
            <span className="dp-rating-count">· {reviewMeta.total_ratings} review{reviewMeta.total_ratings === 1 ? '' : 's'}</span>
          </div>
        )}

        <div className="dp-stat-row">
          <div className="dp-stat">
            <span className="dp-stat-value">{profile.video_count || 0}</span>
            <span className="dp-stat-label">Videos</span>
          </div>
          <div className="dp-stat-divider" />
          <div className="dp-stat">
            <span className="dp-stat-value">{profile.followers?.length || 0}</span>
            <span className="dp-stat-label">Followers</span>
          </div>
          <div className="dp-stat-divider" />
          <div className="dp-stat">
            <span className="dp-stat-value">{profile.following?.length || 0}</span>
            <span className="dp-stat-label">Following</span>
          </div>
        </div>

        {showActions && (
          <div className="dp-hero-actions">
            <button
              onClick={toggleFollow}
              className={`dp-btn ${isFollowing ? 'dp-btn-outline' : 'dp-btn-primary'}`}
              data-testid="follow-btn"
            >
              {isFollowing ? <><UserMinus size={16} /> Following</> : <><UserPlus size={16} /> Follow</>}
            </button>
            <button
              onClick={() => navigate(`/messages?with=${id}`)}
              className="dp-btn dp-btn-outline"
              data-testid="message-btn"
            >
              <MessageSquare size={16} /> Message
            </button>
          </div>
        )}
      </section>

      {/* Body */}
      <section className="dp-body">
        {profile.bio && (
          <div className="dp-card">
            <h3 className="dp-card-title">About</h3>
            <p className="dp-card-text">{profile.bio}</p>
          </div>
        )}

        {profile.experience && (
          <div className="dp-card">
            <h3 className="dp-card-title"><Award size={16} /> Experience</h3>
            <p className="dp-card-text">{profile.experience}</p>
          </div>
        )}

        {profile.desired_outcomes && (
          <div className="dp-card">
            <h3 className="dp-card-title">What you'll take away</h3>
            <p className="dp-card-text">{profile.desired_outcomes}</p>
          </div>
        )}

        {profile.skills?.length > 0 && (
          <div className="dp-card">
            <h3 className="dp-card-title">Skills</h3>
            <div className="dp-skills">
              {profile.skills.map((s, i) => (
                <span key={i} className="dp-skill-chip">{s}</span>
              ))}
            </div>
          </div>
        )}

        {/* Packages */}
        {profile.booking_packages?.length > 0 && (
          <div className="dp-card">
            <h3 className="dp-card-title">Book a session</h3>
            <div className="dp-packages">
              {profile.booking_packages.map((pkg, i) => (
                <div key={pkg.id || i} className="dp-package" data-testid={`package-${i}`}>
                  <div className="dp-pkg-left">
                    <p className="dp-pkg-name">{pkg.name || `${pkg.minutes || 15} min session`}</p>
                    <p className="dp-pkg-meta">
                      <Clock size={13} /> {pkg.minutes || 15} minutes
                    </p>
                  </div>
                  <div className="dp-pkg-right">
                    <span className="dp-pkg-price">
                      <span className="dp-currency">$</span>{grossFromNet(pkg.price).toFixed(grossFromNet(pkg.price) % 1 ? 2 : 0)}
                    </span>
                    {showActions && (
                      <button
                        className="dp-book-btn"
                        onClick={() => bookSession(pkg)}
                        data-testid={`book-btn-${i}`}
                      >
                        <DollarSign size={14} /> Book
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reviews */}
        {reviews.length > 0 && (
          <div className="dp-card" data-testid="dp-reviews">
            <h3 className="dp-card-title">
              <UsersIcon size={16} /> Recent reviews
            </h3>
            <div className="dp-reviews">
              {reviews.slice(0, 10).map((r, i) => (
                <div key={r.id || i} className="dp-review">
                  <div className="dp-review-head">
                    <StarRow rating={r.overall_rating || 0} size={12} />
                    <span className="dp-review-num">{Number(r.overall_rating || 0).toFixed(1)}</span>
                    <span className="dp-review-date">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }) : ''}
                    </span>
                  </div>
                  {r.review_text && <p className="dp-review-text">{r.review_text}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Request Session modal (calendar-first, pre-auth Stripe) */}
      {booking?.pkg && (
        <div
          onClick={() => !submitting && setBooking(null)}
          data-testid="request-session-modal"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 20, padding: 24, maxWidth: 460, width: '100%', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}
          >
            <button onClick={() => setBooking(null)} aria-label="Close" style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer' }}>
              <X size={22} />
            </button>
            <h2 style={{ textAlign: 'center', fontFamily: 'Fraunces, serif', fontWeight: 800, fontSize: 26, margin: '0 0 4px', color: '#111' }}>Request Session</h2>
            <p style={{ textAlign: 'center', color: '#6B7280', margin: '0 0 20px' }}>{booking.pkg.name || `${booking.pkg.minutes || 30}-min session`} · {fmtMoney(grossFromNet(booking.pkg.price))} AUD</p>
            <FeeLine net={booking.pkg.price} />

            <div style={{ background: '#111', borderRadius: 14, padding: 14, marginBottom: 12 }}>
              <p style={{ margin: 0, textAlign: 'center', color: '#E07A5F', fontSize: 12, fontWeight: 700, letterSpacing: 0.4 }}>
                {mentorKnowsTz
                  ? `MENTOR'S TIME${tzLabel(mentorTz, profile?.location) ? ` · ${tzLabel(mentorTz, profile?.location).toUpperCase()}` : ''}`
                  : 'YOUR LOCAL TIME'}
              </p>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <input
                  type="date"
                  value={reqDate}
                  onChange={(e) => setReqDate(e.target.value)}
                  data-testid="request-date-input"
                  min={new Date().toISOString().slice(0,10)}
                  style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid #2A2A2A', background: '#1A1A1A', color: '#fff', fontSize: 15 }}
                />
                <input
                  type="time"
                  value={reqTime}
                  onChange={(e) => setReqTime(e.target.value)}
                  data-testid="request-time-input"
                  style={{ width: 120, padding: '12px', borderRadius: 10, border: '2px solid #fff', background: '#1A1A1A', color: '#fff', fontSize: 15 }}
                />
              </div>
              {mentorTimeDisplay && (
                <p data-testid="mentor-local-time" style={{ margin: '10px 0 0', textAlign: 'center', color: '#D1D5DB', fontSize: 13 }}>
                  {mentorTimeDisplay}
                </p>
              )}
            </div>

            {!mentorKnowsTz && (
              <div
                data-testid="mentor-tz-unknown-notice"
                style={{ background: '#78350F', color: '#FEF3C7', fontSize: 13, padding: '10px 14px', borderRadius: 10, margin: '0 0 12px', lineHeight: 1.4 }}
              >
                <strong>Heads-up:</strong> {profile?.full_name?.split(' ')[0] || 'This mentor'} hasn’t set their timezone yet. We can’t show their local time, so pick carefully — they may be asleep when it’s daytime for you.
              </div>
            )}

            {yourTime && showYourLocalPanel && (
              <div style={{ background: '#0F2A24', borderRadius: 14, padding: 14, marginBottom: 20, textAlign: 'center' }}>
                <p style={{ margin: 0, color: '#4ADE80', fontSize: 12, fontWeight: 700, letterSpacing: 0.4 }}>
                  YOUR LOCAL TIME{tzLabel(myTz, user?.location) ? ` · ${tzLabel(myTz, user?.location).toUpperCase()}` : ''}
                </p>
                <p data-testid="your-local-time" style={{ margin: '6px 0 0', color: '#fff', fontFamily: 'Fraunces, serif', fontWeight: 700, fontSize: 22 }}>
                  {yourTime}
                </p>
              </div>
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6B7280', fontSize: 15, marginBottom: 8 }}>
              <MessageSquare size={16} /> What would you like to learn?
            </label>
            <textarea
              value={reqNote}
              onChange={(e) => setReqNote(e.target.value.slice(0, 400))}
              placeholder="A short message on the topic (min 5 chars)…"
              data-testid="request-note-input"
              rows={4}
              style={{ width: '100%', padding: 14, borderRadius: 12, border: '1px solid #2A2A2A', background: '#1A1A1A', color: '#fff', fontFamily: 'inherit', fontSize: 15, resize: 'vertical', boxSizing: 'border-box' }}
            />
            <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 13, margin: '8px 0 16px' }}>{reqNote.length}/400</p>

            <div style={{ background: '#FEF3C7', color: '#78350F', fontSize: 13, padding: '12px 14px', borderRadius: 10, margin: '0 0 16px', lineHeight: 1.45 }}>
              <p style={{ margin: 0, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Info size={14} /> This is a card <u>hold</u>, not a charge.
              </p>
              <p style={{ margin: '6px 0 0' }}>
                {fmtMoney(grossFromNet(booking.pkg.price))} will show as pending on your statement right away, but the money only actually moves once your mentor joins the meeting at the agreed time. If they no-show, cancel, or decline, the hold is released automatically — usually within a day or two depending on your bank.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setBooking(null)}
                disabled={submitting}
                style={{ flex: 1, padding: 14, borderRadius: 999, border: 'none', background: '#111', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
              >Back</button>
              <button
                onClick={submitBookingRequest}
                disabled={submitting}
                data-testid="request-submit-btn"
                style={{ flex: 2, padding: 14, borderRadius: 999, border: 'none', background: '#E07A5F', color: '#fff', fontWeight: 700, fontSize: 15, cursor: submitting ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                <DollarSign size={16} /> {submitting ? 'Redirecting…' : `Pay ${fmtMoney(grossFromNet(booking.pkg.price))} & Request`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailedProfile;
